export type MetadataEvidenceCategory =
  | 'your_edit_kept'
  | 'frame_cap_kept_current_title'
  | 'fallback_review'
  | 'ai'
  | 'not_generated'
  | 'unknown';

export interface MetadataFieldEvidence {
  readonly field: string;
  readonly category: MetadataEvidenceCategory;
  readonly label:
    | 'Your edit kept'
    | 'Frame cap kept current title'
    | 'Fallback review'
    | 'AI'
    | 'Not generated'
    | 'Unknown';
}

const EVIDENCE_LABELS: Record<MetadataEvidenceCategory, MetadataFieldEvidence['label']> = {
  your_edit_kept: 'Your edit kept',
  frame_cap_kept_current_title: 'Frame cap kept current title',
  fallback_review: 'Fallback review',
  ai: 'AI',
  not_generated: 'Not generated',
  unknown: 'Unknown',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function categoryForField(
  field: string,
  perField: Record<string, unknown> | null,
  skippedUserEdited: Record<string, unknown> | null,
  skippedFrameCapped: Record<string, unknown> | null,
): MetadataEvidenceCategory {
  if (skippedUserEdited?.[field] === true) return 'your_edit_kept';
  if (skippedFrameCapped?.[field] === true) return 'frame_cap_kept_current_title';

  const fieldEvidence = asRecord(perField?.[field]);
  if (fieldEvidence?.fallback_fired === true) return 'fallback_review';
  if (fieldEvidence?.applied === true) return 'ai';
  if (fieldEvidence?.applied === false && fieldEvidence.fallback_fired === false) {
    return 'not_generated';
  }
  return 'unknown';
}

export function buildMetadataFieldEvidence(postgen: unknown): readonly MetadataFieldEvidence[] {
  const postgenEvidence = asRecord(postgen);
  const metadata = asRecord(postgenEvidence?.metadata);
  const asyncEvidence = asRecord(postgenEvidence?.async);
  const perField = asRecord(metadata?.per_field);
  const skippedUserEdited = asRecord(asyncEvidence?.skipped_user_edited);
  const skippedFrameCapped = asRecord(asyncEvidence?.skipped_frame_capped);
  const fields = new Set([
    ...Object.keys(perField ?? {}),
    ...Object.keys(skippedUserEdited ?? {}),
    ...Object.keys(skippedFrameCapped ?? {}),
  ]);

  return Array.from(fields, (field) => {
    const category = categoryForField(field, perField, skippedUserEdited, skippedFrameCapped);
    return { field, category, label: EVIDENCE_LABELS[category] };
  });
}
